'use client';

interface Turnstile {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      appearance: 'interaction-only';
      callback: (token: string) => void;
      'error-callback': () => void;
      'expired-callback': () => void;
      'before-interactive-callback': () => void;
      'after-interactive-callback': () => void;
    },
  ): string;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TIMEOUT_MS = 120_000;
const SCRIPT_TIMEOUT_MS = 15_000;
let scriptReady: Promise<void> | undefined;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptReady) {
    scriptReady = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      const fail = (): void => {
        clearTimeout(timer);
        script.remove();
        scriptReady = undefined;
        reject(new Error('Verification check could not load'));
      };
      const timer = setTimeout(fail, SCRIPT_TIMEOUT_MS);
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }
  return scriptReady;
}

/** Each billable request gets a fresh single-use token; keys and tokens are never persisted. */
export async function requestTurnstileToken(
  siteKey: string | null | undefined,
  action: string,
  ariaLabel: string,
): Promise<string | undefined> {
  if (!siteKey) return undefined;
  await loadTurnstile();
  const turnstile = window.turnstile;
  if (!turnstile) throw new Error('Verification check unavailable');
  return new Promise<string>((resolve, reject) => {
    const container = document.createElement('div');
    container.setAttribute('role', 'dialog');
    container.setAttribute('aria-label', ariaLabel);
    container.className = 'SmsPhoneField__botCheck';
    // Shown only when Cloudflare asks for a click, so the checkbox never appears unexplained over the page.
    const prompt = document.createElement('p');
    prompt.className = 'SmsPhoneField__botCheckPrompt';
    prompt.textContent = 'Please confirm you are human to continue.';
    container.appendChild(prompt);
    (document.querySelector('dialog[open]') ?? document.body).appendChild(container);
    let widget: string | undefined;
    let settled = false;
    const finish = (token?: string): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (widget) turnstile.remove(widget);
      container.remove();
      if (token) resolve(token);
      else reject(new Error('Verification check failed. Try again.'));
    };
    const timer = setTimeout(() => finish(), TIMEOUT_MS);
    try {
      widget = turnstile.render(container, {
        sitekey: siteKey,
        action,
        appearance: 'interaction-only',
        callback: token => finish(token),
        'error-callback': () => finish(),
        'expired-callback': () => finish(),
        'before-interactive-callback': () => container.classList.add('SmsPhoneField__botCheck--interactive'),
        'after-interactive-callback': () => container.classList.remove('SmsPhoneField__botCheck--interactive'),
      });
    } catch {
      finish();
    }
  });
}

export function requestContactVerificationBotToken(siteKey: string | null | undefined): Promise<string | undefined> {
  return requestTurnstileToken(siteKey, 'contact-verification', 'Phone verification check');
}
